import { AnimatePresence, motion } from 'framer-motion'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundSm } from '@apps-common/components/elements/button'
import { InputInfoSM, InputSM, SelectInputSM } from '@apps-common/components/elements/form'
import { VertSpacerMd, VertSpacerXl } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { CidEventSelectModal } from '@apps-common/components/modals/cid-events/CidEventSelectModal'
import { CONTROL_EVENT_STATE_RESTORE, CONTROL_EVENT_STATE_SELECT_OPTIONS } from '@apps-common/config/controls'
import { commonIcons } from '@apps-common/config/icons'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { ICidEventModel } from '@apps-common/types/cid-event-code'

interface IProps {
  stateIndex: number
  disabled: boolean
}

export const ControlEventCheckForm = ({ stateIndex, disabled }: IProps) => {
  const { register, control, formState } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name: `customStates.${stateIndex}.eventChecks`,
    rules: {
      required: false,
      validate: (value) => {
        if (value?.length === 0) {
          return 'A state requires at least one event check.'
        }

        return true
      },
      minLength: 1
    }
  })

  const rootError = (formState as any)?.errors?.customStates?.[stateIndex]?.eventChecks?.root?.message as string
  const errors = (formState as any)?.errors?.customStates?.[stateIndex]?.eventChecks as any

  return (
    <div>
      <div className='px-4 py-2 bg-surface-2 border border-border text-text rounded-[30px] relative'>
        <div className='pl-[10px] flex items-center justify-between'>
          <SectionTitle text='Event checks' classes='!text-[0.9em]' />
          <ModalButton
            component={CidEventSelectModal}
            disabled={disabled}
            confirmCallback={(d: ICidEventModel) =>
              append({
                onState: CONTROL_EVENT_STATE_RESTORE,
                description: d.description,
                eventType: '',
                eventCode: d.code,
                zone: '',
                partition: ''
              })
            }
          >
            <ButtonRoundSm type='div' className='action-success'>
              <commonIcons.add />
            </ButtonRoundSm>
          </ModalButton>
        </div>
      </div>
      <motion.div>
        <AnimatePresence mode='popLayout' initial={false}>
          {fields.map((field: any, index) => (
            <motion.div
              key={`${field.id}.customStates.${stateIndex}.eventChecks`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <VertSpacerXl />
              <div className='px-6 py-4 bg-surface-2 border border-border rounded-[30px] relative p-[10px]'>
                <div className='flex items-center justify-between'>
                  <SectionTitle text={`Event Check #${index + 1}`} classes='ml-2 !text-[0.9em]' />
                  <ButtonRoundSm type='button' className='action-warning' loading={disabled} callback={() => remove(index)}>
                    <commonIcons.remove />
                  </ButtonRoundSm>
                </div>
                <VertSpacerMd />
                <div className='flex flex-col gap-[15px]'>
                  <InputInfoSM
                    id='cid-event-description'
                    label='CID event description'
                    showLabel
                    className='bg-transparent'
                    value={field.description}
                  />
                  <InputInfoSM id='cid-event-code' label='CID event code' showLabel className='bg-transparent' value={field.eventCode} />

                  <SelectInputSM
                    key={`${field.id}.onState`}
                    type='text'
                    placeholder='On state'
                    label='On state'
                    id={`customStates.${stateIndex}.eventChecks.${index}.onState`}
                    showLabel
                    disabled={disabled}
                    registration={register(`customStates.${stateIndex}.eventChecks.${index}.onState`, {
                      required: FORM_DEFAULT_REQUIRED
                    })}
                    options={CONTROL_EVENT_STATE_SELECT_OPTIONS}
                    errors={(errors as any)?.[index]?.onState}
                  />
                  <InputSM
                    key={`${field.id}.zone`}
                    type='text'
                    placeholder='1 to 999'
                    label='Zone/User check (If required)'
                    id={`customStates.${stateIndex}.eventChecks.${index}.zone`}
                    showLabel
                    disabled={disabled}
                    registration={register(`customStates.${stateIndex}.eventChecks.${index}.zone`, {
                      required: false,
                      pattern: {
                        value: /^[0-9]{3}$/,
                        message: '3 numerical characters only e.g. 001'
                      }
                    })}
                    errors={(errors as any)?.[index]?.zone}
                  />
                  <InputSM
                    key={`${field.id}.partition`}
                    type='text'
                    placeholder='0 to 99'
                    label='Partition check (If required)'
                    id={`customStates.${stateIndex}.eventChecks.${index}.partition`}
                    showLabel
                    disabled={disabled}
                    registration={register(`customStates.${stateIndex}.eventChecks.${index}.partition`, {
                      required: false,
                      pattern: {
                        value: /^[0-9]{2}$/,
                        message: '2 numerical characters only e.g. 01'
                      }
                    })}
                    errors={(errors as any)?.[index]?.partition}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {rootError && <p className='pl-[10px] text-[0.8em] text-error jost'>{rootError}</p>}
      <VertSpacerMd />
    </div>
  )
}
